import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Tooltip,
  useTheme,
  alpha,
  Divider,
  InputAdornment,
  Badge,
} from "@mui/material";
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as UserIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  AutoAwesome as SparklesIcon,
  Refresh as ClearIcon,
  Schedule as ClockIcon,
  EventNote as EventIcon,
  Search as SearchIcon,
  Circle as CircleIcon,
  Forum as ForumIcon,
  Group as GroupIcon,
  FolderSpecial as ProjectIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Assets
import aiChatIcon from "assets/img/icons/ai_chat_icon.png";

// Services & Helpers
import { sendChatMessage, confirmAiAction } from "services/aiService";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import AiTaskCard from "./AiTaskCard";

// Redux Store Actions & Selectors
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import {
  selectEmployees,
  selectEmployeeLoading,
} from "store/features/company/employee/employeeSelectors";
import {
  fetchUserConversations,
  getOrCreateDirectConversationThunk,
  fetchConversationMessagesThunk,
  sendChatMessageThunk,
  markConversationReadThunk,
} from "store/features/company/chat/chatActions";
import { setSelectedUser as setSelectedUserRedux } from "store/features/company/chat/chatSlice";
import {
  selectSelectedUser,
  selectConversations,
  selectMessagesMap,
  selectMessagesLoading,
} from "store/features/company/chat/chatSelectors";

const parseTaskCardsFromResponse = (text, resData = {}) => {
  let tasks = [];
  let cleanText = text || "";

  if (resData.tasks && Array.isArray(resData.tasks)) {
    tasks = [...resData.tasks];
  } else if (resData.task && typeof resData.task === "object") {
    tasks = [resData.task];
  }

  if (text && typeof text === "string") {
    const jsonBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/gi;
    let match;
    while ((match = jsonBlockRegex.exec(text)) !== null) {
      try {
        const parsed = JSON.parse(match[1].trim());
        const extracted =
          parsed.tasks ||
          (parsed.task ? [parsed.task] : null) ||
          (Array.isArray(parsed) ? parsed : null);

        if (
          parsed.render === "task_card" ||
          parsed.render === "task" ||
          parsed.type === "render_task_card" ||
          parsed.type === "task_card" ||
          parsed.action === "render_task_card" ||
          (Array.isArray(extracted) &&
            extracted.length > 0 &&
            (extracted[0].title || extracted[0].task_name || extracted[0].id))
        ) {
          if (Array.isArray(extracted) && extracted.length > 0) {
            tasks = [...tasks, ...extracted];
            cleanText = cleanText.replace(match[0], "").trim();
          }
        }
      } catch (e) {
        // Not a JSON task block
      }
    }

    if (
      tasks.length === 0 &&
      text.trim().startsWith("{") &&
      text.trim().endsWith("}")
    ) {
      try {
        const parsed = JSON.parse(text.trim());
        const extracted = parsed.tasks || (parsed.task ? [parsed.task] : null);

        if (
          parsed.render === "task_card" ||
          parsed.render === "task" ||
          parsed.type === "render_task_card" ||
          parsed.type === "task_card" ||
          parsed.action === "render_task_card" ||
          (Array.isArray(extracted) && extracted.length > 0)
        ) {
          if (Array.isArray(extracted) && extracted.length > 0) {
            tasks = [...tasks, ...extracted];
            cleanText =
              parsed.message || parsed.text || "Here is the requested task:";
          }
        }
      } catch (e) {
        // Ignored
      }
    }
  }

  return { tasks, cleanText };
};

const getInitials = (firstName, lastName) => {
  const f = firstName ? firstName.charAt(0).toUpperCase() : "";
  const l = lastName ? lastName.charAt(0).toUpperCase() : "";
  return f + l || "E";
};

const getInitialsFromName = (name) => {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getAvatarColor = (name) => {
  const colors = [
    "#3b82f6",
    "#10b981",
    "#8b5cf6",
    "#ec4899",
    "#f59e0b",
    "#06b6d4",
    "#6366f1",
    "#14b8a6",
  ];
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const AI_USER = {
  id: "ai-assistant",
  name: "AI Enterprise Assistant",
  role: "AI Automation & Workflows",
  isAi: true,
};

const Chat = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Current logged in user info
  const userRaw = getSecureLocalItem("user");
  const currentUser =
    typeof userRaw === "string" ? JSON.parse(userRaw) : userRaw || {};
  const currentUserId = String(
    currentUser.user_id || currentUser.id || currentUser.employee_id || "",
  );
  const currentUserName =
    `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim() ||
    currentUser.name ||
    currentUser.email ||
    "";
  const currentUserAvatar =
    currentUser.profile_image || currentUser.avatar || "";
  const currentUserRole =
    currentUser.role ||
    currentUser.role_name ||
    currentUser.designation_name ||
    "Employee";

  // Redux State Selectors
  const employees = useSelector(selectEmployees);
  const loadingEmployees = useSelector(selectEmployeeLoading);
  const selectedUser = useSelector(selectSelectedUser) || AI_USER;
  const conversations = useSelector(selectConversations);
  const messagesMap = useSelector(selectMessagesMap);
  const loadingMessages = useSelector(selectMessagesLoading);

  // Local UI State
  const [activeTab, setActiveTab] = useState("direct"); // "direct" | "groups"
  const [searchQuery, setSearchQuery] = useState("");
  const [inputMessage, setInputMessage] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMessages, setAiMessages] = useState([
    {
      id: "welcome-1",
      sender: "bot",
      text: "Hello! I am your AI Assistant. How can I help you today?",
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    },
  ]);

  const chatEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [aiMessages, messagesMap, selectedUser, aiLoading, loadingMessages]);

  // Fetch employees & conversations via Redux Thunks on mount
  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchUserConversations());
  }, [dispatch]);

  // Filter active employees for search
  const activeEmployees = (employees || []).filter((emp) => {
    if (!emp) return false;
    if (emp.status !== undefined) {
      const s = String(emp.status).toLowerCase();
      return s === "active" || s === "1" || s === "true";
    }
    if (emp.is_active !== undefined) {
      return Boolean(emp.is_active);
    }
    return true;
  });

  const filteredEmployees = activeEmployees.filter((emp) => {
    const fullName = `${emp.first_name || ""} ${emp.last_name || ""
      }`.toLowerCase();
    const email = (emp.email || "").toLowerCase();
    const designation = (
      emp.designation_name ||
      emp.department_name ||
      ""
    ).toLowerCase();
    const query = searchQuery.toLowerCase();
    return (
      fullName.includes(query) ||
      email.includes(query) ||
      designation.includes(query)
    );
  });

  // Filter conversations by Direct vs Group
  const directConversations = (conversations || []).filter((conv) => {
    const isProject = conv.type === "project" || Boolean(conv.projectId);
    const isGroup = conv.type === "group" || isProject;
    return !isGroup;
  });

  const groupConversations = (conversations || []).filter((conv) => {
    const isProject = conv.type === "project" || Boolean(conv.projectId);
    const isGroup = conv.type === "group" || isProject;
    return isGroup;
  });

  const directUnreadCount = directConversations.reduce(
    (acc, conv) => acc + (conv.unreadCounts?.[currentUserId] || 0),
    0,
  );

  const groupUnreadCount = groupConversations.reduce(
    (acc, conv) => acc + (conv.unreadCounts?.[currentUserId] || 0),
    0,
  );

  // Handle selecting a Recent Conversation
  const handleSelectConversation = async (conv) => {
    if (!conv) return;

    let targetUserObj = {};
    if (conv.type === "group" || conv.type === "project" || conv.projectId) {
      const isProject = conv.type === "project" || Boolean(conv.projectId);
      targetUserObj = {
        id: conv._id,
        name: conv.name || (isProject ? "Project Group" : "Group Chat"),
        role: isProject ? "Project Group" : "Group Conversation",
        avatar: conv.avatar || "",
        isAi: false,
        isGroup: true,
        isProject,
        isRemoved: Boolean(conv.isRemoved),
        conversationId: conv._id,
      };
      setActiveTab("groups");
    } else {
      const otherPart =
        (conv.participants || []).find(
          (p) => String(p.userId) !== String(currentUserId),
        ) ||
        (conv.participants || [])[0] ||
        {};

      targetUserObj = {
        id: otherPart.userId || conv._id,
        name: otherPart.name || "Direct Chat",
        role: otherPart.role || "Team Member",
        avatar: otherPart.avatar || "",
        email: otherPart.email || "",
        isAi: false,
        isGroup: false,
        isProject: false,
        isRemoved: false,
        conversationId: conv._id,
      };
      setActiveTab("direct");
    }

    dispatch(setSelectedUserRedux(targetUserObj));
    await dispatch(
      fetchConversationMessagesThunk(conv._id, { page: 1, limit: 50 }),
    );
    dispatch(markConversationReadThunk(conv._id));
  };

  // Handle selecting an employee from search results
  const handleSelectEmployee = async (emp) => {
    const empName =
      `${emp.first_name || ""} ${emp.last_name || ""}`.trim() ||
      emp.email ||
      "Employee";
    const empRole =
      emp.designation_name || emp.department_name || emp.role || "Team Member";

    const userObj = {
      id: emp.id,
      name: empName,
      role: empRole,
      email: emp.email,
      avatar: emp.avatar || emp.profile_image,
      isAi: false,
      conversationId: null,
      raw: emp,
    };

    dispatch(setSelectedUserRedux(userObj));
    setSearchQuery(""); // Reset search so recent chats view is active

    // Get or Create Direct Conversation
    const res = await dispatch(
      getOrCreateDirectConversationThunk({
        recipientId: String(emp.id),
        recipientName: empName,
        recipientAvatar: emp.avatar || emp.profile_image || "",
        recipientRole: empRole,
        senderName: currentUserName,
        senderAvatar: currentUserAvatar,
        senderRole: currentUserRole,
      }),
    );

    if (res?.success && res?.data?._id) {
      const conversationId = res.data._id;
      dispatch(setSelectedUserRedux({ ...userObj, conversationId }));
      await dispatch(
        fetchConversationMessagesThunk(conversationId, { page: 1, limit: 50 }),
      );
      dispatch(markConversationReadThunk(conversationId));
      // Refresh conversations list to show new chat in recent list
      dispatch(fetchUserConversations());
    }
  };

  // Send Message Handler
  const handleSendMessage = async (textToSend) => {
    const text = (textToSend || inputMessage).trim();
    if (!text) return;

    if (selectedUser.isRemoved) {
      toast.error(
        "You are no longer a member of this project group and cannot send messages.",
      );
      return;
    }

    if (selectedUser.isAi) {
      // AI CHAT FLOW
      const userMessageId = `user-${Date.now()}`;
      const userMsg = {
        id: userMessageId,
        sender: "user",
        text,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setAiMessages((prev) => [...prev, userMsg]);
      if (!textToSend) setInputMessage("");
      setAiLoading(true);

      try {
        const response = await sendChatMessage(text);
        const resData = response?.data || {};
        const botAnswer =
          resData.answer || response?.meta?.message || "Response received.";

        const { tasks: parsedTasks, cleanText } = parseTaskCardsFromResponse(
          botAnswer,
          resData,
        );

        const botMsg = {
          id: `bot-${Date.now()}`,
          sender: "bot",
          text:
            cleanText ||
            (parsedTasks.length > 0 ? "Here are the task details:" : botAnswer),
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          requiresConfirmation: Boolean(resData.requires_confirmation),
          pendingActionId: resData.pending_action_id,
          draft: resData.draft || null,
          intent: resData.intent || null,
          tasks: parsedTasks.length > 0 ? parsedTasks : null,
          expiresAt: resData.expires_at || null,
          confirmationStatus: null,
          isConfirming: false,
        };

        setAiMessages((prev) => [...prev, botMsg]);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.meta?.message ||
          err?.message ||
          "Failed to generate AI response",
        );
        setAiMessages((prev) => [
          ...prev,
          {
            id: `bot-err-${Date.now()}`,
            sender: "bot",
            text: "Sorry, I encountered an issue processing your request. Please try again.",
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            isError: true,
          },
        ]);
      } finally {
        setAiLoading(false);
      }
    } else {
      // REDUX + MONGODB 1-ON-1 CHAT FLOW
      if (!textToSend) setInputMessage("");
      let convId = selectedUser.conversationId;

      if (!convId) {
        const res = await dispatch(
          getOrCreateDirectConversationThunk({
            recipientId: String(selectedUser.id),
            recipientName: selectedUser.name,
            recipientAvatar: selectedUser.avatar || "",
            recipientRole: selectedUser.role || "",
            senderName: currentUserName,
            senderAvatar: currentUserAvatar,
            senderRole: currentUserRole,
          }),
        );
        if (res?.success && res?.data?._id) {
          convId = res.data._id;
          dispatch(
            setSelectedUserRedux({ ...selectedUser, conversationId: convId }),
          );
        }
      }

      if (!convId) {
        toast.error("Failed to establish conversation session.");
        return;
      }

      // Dispatch Redux Thunk Action to send message to MongoDB
      await dispatch(
        sendChatMessageThunk({
          conversationId: convId,
          text,
          messageType: "text",
          attachments: [],
          senderName: currentUserName,
          senderAvatar: currentUserAvatar,
          senderRole: currentUserRole,
        }),
      );

      // Refresh recent conversations
      dispatch(fetchUserConversations());
    }
  };

  const handleConfirmAction = async (msgId, pendingActionId, confirmStatus) => {
    if (!pendingActionId) return;

    setAiMessages((prev) =>
      prev.map((msg) =>
        msg.id === msgId ? { ...msg, isConfirming: true } : msg,
      ),
    );

    try {
      const response = await confirmAiAction(pendingActionId, confirmStatus);
      const resData = response?.data || {};
      const resultAnswer =
        resData.answer ||
        response?.meta?.message ||
        (confirmStatus
          ? "Action confirmed successfully."
          : "Action cancelled.");

      const { tasks: confirmTasks, cleanText: confirmCleanText } =
        parseTaskCardsFromResponse(resultAnswer, resData);

      setAiMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === msgId) {
            return {
              ...msg,
              isConfirming: false,
              confirmationStatus: confirmStatus ? "confirmed" : "cancelled",
              confirmResultAnswer: confirmCleanText || resultAnswer,
              tasks: confirmTasks.length > 0 ? confirmTasks : msg.tasks,
            };
          }
          return msg;
        }),
      );

      toast.success(
        confirmStatus ? "Action confirmed successfully!" : "Action cancelled.",
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err?.meta?.message || err?.message || "Failed to process confirmation",
      );
      setAiMessages((prev) =>
        prev.map((msg) =>
          msg.id === msgId ? { ...msg, isConfirming: false } : msg,
        ),
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Safely extract raw messages array for active conversation
  const activeConversationId = selectedUser.conversationId;
  const storeMessages = messagesMap[activeConversationId];
  const rawEmployeeMessages = Array.isArray(storeMessages)
    ? storeMessages
    : Array.isArray(storeMessages?.messages)
      ? storeMessages.messages
      : [];

  const currentEmployeeMessages = rawEmployeeMessages.map((m) => {
    const isUser = String(m.senderId) === currentUserId;
    let senderName = m.senderName;
    let senderAvatar = m.senderAvatar;

    if (isUser) {
      senderName = currentUserName || "You";
      senderAvatar = currentUserAvatar;
    } else if (!senderName || !senderAvatar) {
      const foundEmp = (employees || []).find(
        (e) =>
          String(e.id || e._id || e.user_id || e.employee_id) ===
          String(m.senderId),
      );
      if (foundEmp) {
        if (!senderName) {
          senderName =
            `${foundEmp.first_name || ""} ${foundEmp.last_name || ""}`.trim() ||
            foundEmp.name ||
            foundEmp.email;
        }
        if (!senderAvatar) {
          senderAvatar = foundEmp.avatar || foundEmp.profile_image || "";
        }
      }
    }

    return {
      id: m._id || m.id,
      sender: isUser ? "user" : "other",
      senderId: m.senderId,
      senderName: senderName || "Member",
      senderAvatar: senderAvatar || "",
      text: m.text,
      timestamp: m.createdAt
        ? new Date(m.createdAt).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
        : new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
    };
  });

  const isSearching = searchQuery.trim().length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        width: "100%",
        background: isDark
          ? "linear-gradient(180deg, #0f172a 0%, #090d16 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        overflow: "hidden",
      }}
    >
      {/* LEFT SIDEBAR - Fixed height with scrollable content */}
      <Box
        sx={{
          minWidth: { xs: "320px", sm: "360px" },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRight: `1px solid ${isDark ? alpha("#cbd5e1", 0.08) : alpha("#e2e8f0", 0.8)
            }`,
          backgroundColor: isDark
            ? alpha("#0f172a", 0.95)
            : alpha("#f8fafc", 0.95),
          overflow: "hidden",
        }}
      >
        {/* Sidebar Header - Search input & Toggle */}
        <Box sx={{ p: 2.5, pb: 1.5, flexShrink: 0 }}>
          {/* <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem", color: isDark ? "#f3f4f6" : "#0f172a" }}>
              Messages & Team
            </Typography>
          </Box> */}

          <TextField
            fullWidth
            size="small"
            placeholder={
              activeTab === "direct"
                ? "Search employee to chat..."
                : "Search groups..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: isDark ? "#1e293b" : "#ffffff",
                fontSize: "0.85rem",
                "& fieldset": {
                  borderColor: isDark
                    ? alpha("#334155", 0.8)
                    : alpha("#cbd5e1", 0.8),
                },
                "&:hover fieldset": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
          />

          {/* TOGGLE BUTTONS FOR DIRECT MESSAGES & GROUPS */}
          <Box
            sx={{
              display: "flex",
              backgroundColor: isDark
                ? alpha("#1e293b", 0.7)
                : alpha("#e2e8f0", 0.7),
              borderRadius: "12px",
              p: "4px",
              gap: "4px",
            }}
          >
            <Button
              fullWidth
              size="small"
              onClick={() => setActiveTab("direct")}
              startIcon={<UserIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: "9px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.8rem",
                py: 0.75,
                color:
                  activeTab === "direct"
                    ? isDark
                      ? "#ffffff"
                      : "#0f172a"
                    : isDark
                      ? "#94a3b8"
                      : "#64748b",
                backgroundColor:
                  activeTab === "direct"
                    ? isDark
                      ? "#3b82f6"
                      : "#ffffff"
                    : "transparent",
                boxShadow:
                  activeTab === "direct"
                    ? isDark
                      ? "0 2px 8px rgba(59, 130, 246, 0.4)"
                      : "0 2px 6px rgba(0,0,0,0.08)"
                    : "none",
                "&:hover": {
                  backgroundColor:
                    activeTab === "direct"
                      ? isDark
                        ? "#2563eb"
                        : "#ffffff"
                      : isDark
                        ? alpha("#cbd5e1", 0.08)
                        : alpha("#000000", 0.04),
                },
              }}
            >
              Messages
              {directUnreadCount > 0 && (
                <Chip
                  label={directUnreadCount}
                  size="small"
                  sx={{
                    height: 16,
                    minWidth: 16,
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    ml: 0.75,
                    backgroundColor:
                      activeTab === "direct" ? "#ef4444" : "#3b82f6",
                    color: "#ffffff",
                  }}
                />
              )}
            </Button>

            <Button
              fullWidth
              size="small"
              onClick={() => setActiveTab("groups")}
              startIcon={<GroupIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: "9px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.8rem",
                py: 0.75,
                color:
                  activeTab === "groups"
                    ? isDark
                      ? "#ffffff"
                      : "#0f172a"
                    : isDark
                      ? "#94a3b8"
                      : "#64748b",
                backgroundColor:
                  activeTab === "groups"
                    ? isDark
                      ? "#3b82f6"
                      : "#ffffff"
                    : "transparent",
                boxShadow:
                  activeTab === "groups"
                    ? isDark
                      ? "0 2px 8px rgba(59, 130, 246, 0.4)"
                      : "0 2px 6px rgba(0,0,0,0.08)"
                    : "none",
                "&:hover": {
                  backgroundColor:
                    activeTab === "groups"
                      ? isDark
                        ? "#2563eb"
                        : "#ffffff"
                      : isDark
                        ? alpha("#cbd5e1", 0.08)
                        : alpha("#000000", 0.04),
                },
              }}
            >
              Groups
              {groupUnreadCount > 0 && (
                <Chip
                  label={groupUnreadCount}
                  size="small"
                  sx={{
                    height: 16,
                    minWidth: 16,
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    ml: 0.75,
                    backgroundColor:
                      activeTab === "groups" ? "#ef4444" : "#3b82f6",
                    color: "#ffffff",
                  }}
                />
              )}
            </Button>
          </Box>
        </Box>

        <Divider
          sx={{
            borderColor: isDark
              ? alpha("#cbd5e1", 0.08)
              : alpha("#e2e8f0", 0.8),
            flexShrink: 0,
          }}
        />

        {/* Sidebar Content - Scrollable */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            "&::-webkit-scrollbar": { width: "5px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDark
                ? alpha("#94a3b8", 0.2)
                : alpha("#94a3b8", 0.3),
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          {activeTab === "direct" ? (
            <>
              {/* AI ASSISTANT INCLUDED IN DIRECT MESSAGES */}
              {(!isSearching ||
                "evoai".includes(searchQuery.toLowerCase()) ||
                "ai".includes(searchQuery.toLowerCase()) ||
                "assistant".includes(searchQuery.toLowerCase())) && (
                  <>
                    {/* <Typography
                    variant="caption"
                    sx={{
                      px: 1.5,
                      pt: 0.5,
                      fontWeight: 700,
                      fontSize: "0.68rem",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: isDark ? "#94a3b8" : "#64748b",
                      flexShrink: 0,
                    }}
                  >
                    AI Assistant
                  </Typography> */}

                    {/* ai chat for future release */}
                    {/* <Paper
                    elevation={0}
                    onClick={() => {
                      dispatch(setSelectedUserRedux(AI_USER));
                      setActiveTab("direct");
                    }}
                    sx={{
                      p: 1.5,
                      borderRadius: "14px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      transition: "all 0.2s ease",
                      flexShrink: 0,
                      backgroundColor: selectedUser.isAi
                        ? isDark
                          ? alpha("#f59e0b", 0.15)
                          : alpha("#f59e0b", 0.12)
                        : isDark
                        ? alpha("#1e293b", 0.5)
                        : "#ffffff",
                      border: `1px solid ${
                        selectedUser.isAi
                          ? alpha("#f59e0b", 0.4)
                          : isDark
                          ? alpha("#cbd5e1", 0.05)
                          : alpha("#e2e8f0", 0.6)
                      }`,
                      boxShadow: selectedUser.isAi
                        ? "0 4px 12px rgba(245, 158, 11, 0.15)"
                        : "none",
                      "&:hover": {
                        backgroundColor: selectedUser.isAi
                          ? isDark
                            ? alpha("#f59e0b", 0.2)
                            : alpha("#f59e0b", 0.15)
                          : isDark
                          ? alpha("#1e293b", 0.8)
                          : alpha("#f1f5f9", 0.8),
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <Avatar
                        src={aiChatIcon}
                        sx={{
                          width: 42,
                          height: 42,
                          boxShadow: "0 0 12px rgba(245, 158, 11, 0.35)",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: "#22c55e",
                          border: `2px solid ${isDark ? "#0f172a" : "#ffffff"}`,
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          noWrap
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            color: selectedUser.isAi
                              ? isDark
                                ? "#fcd34d"
                                : "#b45309"
                              : isDark
                              ? "#f3f4f6"
                              : "#0f172a",
                          }}
                        >
                          EvoAI
                        </Typography>
                        <Chip
                          label="AI"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.6rem",
                            fontWeight: 800,
                            backgroundColor: alpha("#f59e0b", 0.2),
                            color: "#f59e0b",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.72rem",
                          display: "block",
                        }}
                      >
                        Your AI Assistant
                      </Typography>
                    </Box>
                  </Paper> */}
                  </>
                )}

              {!isSearching ? (
                /* DIRECT MESSAGES LIST */
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1.5,
                      pt: 1.5,
                      pb: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    {/* <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: isDark ? "#94a3b8" : "#64748b",
                      }}
                    >
                      Direct Messages ({directConversations.length})
                    </Typography> */}
                  </Box>

                  {directConversations.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        textAlign: "center",
                        backgroundColor: "transparent",
                        border: `1px dashed ${isDark
                            ? alpha("#cbd5e1", 0.15)
                            : alpha("#e2e8f0", 0.8)
                          }`,
                        borderRadius: "12px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        No direct messages yet.
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "primary.main", fontWeight: 600 }}
                      >
                        Search an employee above to start chatting!
                      </Typography>
                    </Paper>
                  ) : (
                    directConversations.map((conv) => {
                      const otherParticipant =
                        (conv.participants || []).find(
                          (p) => String(p.userId) !== String(currentUserId),
                        ) ||
                        (conv.participants || [])[0] ||
                        {};

                      const displayName =
                        otherParticipant.name || "Direct Chat";
                      const displayAvatar = otherParticipant.avatar;

                      const isSelected =
                        !selectedUser.isAi &&
                        String(selectedUser.conversationId) ===
                        String(conv._id);

                      const unreadCount =
                        conv.unreadCounts?.[currentUserId] || 0;
                      const lastMsgText =
                        conv.lastMessage?.text || "Conversation started";
                      const lastMsgTime = conv.lastMessage?.createdAt
                        ? new Date(
                          conv.lastMessage.createdAt,
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                        : "";

                      return (
                        <Paper
                          key={conv._id}
                          elevation={0}
                          onClick={() => handleSelectConversation(conv)}
                          sx={{
                            p: 1.25,
                            px: 1.5,
                            borderRadius: "12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            transition: "all 0.2s ease",
                            flexShrink: 0,
                            backgroundColor: isSelected
                              ? isDark
                                ? alpha("#3b82f6", 0.18)
                                : alpha("#3b82f6", 0.1)
                              : isDark
                                ? alpha("#1e293b", 0.3)
                                : "#ffffff",
                            border: `1px solid ${isSelected
                                ? alpha("#3b82f6", 0.4)
                                : isDark
                                  ? alpha("#cbd5e1", 0.05)
                                  : alpha("#e2e8f0", 0.6)
                              }`,
                            "&:hover": {
                              backgroundColor: isSelected
                                ? isDark
                                  ? alpha("#3b82f6", 0.22)
                                  : alpha("#3b82f6", 0.15)
                                : isDark
                                  ? alpha("#1e293b", 0.7)
                                  : alpha("#f1f5f9", 0.8),
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Box sx={{ position: "relative" }}>
                            <Avatar
                              src={displayAvatar}
                              sx={{
                                width: 38,
                                height: 38,
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                backgroundColor: getAvatarColor(displayName),
                                color: "#ffffff",
                              }}
                            >
                              {getInitials(
                                displayName.split(" ")[0],
                                displayName.split(" ")[1],
                              )}
                            </Avatar>
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: "#22c55e",
                                border: `2px solid ${isDark ? "#0f172a" : "#ffffff"
                                  }`,
                              }}
                            />
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                noWrap
                                sx={{
                                  fontWeight: isSelected ? 700 : 600,
                                  fontSize: "0.85rem",
                                  color: isSelected
                                    ? isDark
                                      ? "#60a5fa"
                                      : "#1d4ed8"
                                    : isDark
                                      ? "#f3f4f6"
                                      : "#0f172a",
                                }}
                              >
                                {displayName}
                              </Typography>
                              {lastMsgTime && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.68rem",
                                    color: "text.secondary",
                                  }}
                                >
                                  {lastMsgTime}
                                </Typography>
                              )}
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mt: 0.25,
                              }}
                            >
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.72rem",
                                  maxWidth: "180px",
                                }}
                              >
                                {lastMsgText}
                              </Typography>
                              {unreadCount > 0 && (
                                <Chip
                                  label={unreadCount}
                                  size="small"
                                  sx={{
                                    height: 16,
                                    minWidth: 16,
                                    fontSize: "0.62rem",
                                    fontWeight: 800,
                                    backgroundColor: "#3b82f6",
                                    color: "#ffffff",
                                    px: 0.5,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })
                  )}
                </>
              ) : (
                /* SEARCH RESULTS FOR DIRECT MESSAGES */
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1.5,
                      pt: 1.5,
                      pb: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: isDark ? "#94a3b8" : "#64748b",
                      }}
                    >
                      Search Results ({filteredEmployees.length})
                    </Typography>
                  </Box>

                  {loadingEmployees ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <CircularProgress
                        size={24}
                        sx={{ color: "primary.main" }}
                      />
                    </Box>
                  ) : filteredEmployees.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor: "transparent",
                        border: `1px dashed ${isDark
                            ? alpha("#cbd5e1", 0.15)
                            : alpha("#e2e8f0", 0.8)
                          }`,
                        borderRadius: "12px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        No active employees matching "{searchQuery}"
                      </Typography>
                    </Paper>
                  ) : (
                    filteredEmployees.map((emp) => {
                      const empName =
                        `${emp.first_name || ""} ${emp.last_name || ""
                          }`.trim() ||
                        emp.email ||
                        "Employee";
                      const isSelected =
                        !selectedUser.isAi &&
                        String(selectedUser.id) === String(emp.id);
                      const avatarBg = getAvatarColor(empName);
                      const empRole =
                        emp.designation_name ||
                        emp.department_name ||
                        emp.role ||
                        "Team Member";

                      return (
                        <Paper
                          key={emp.id || emp.employee_code || emp.email}
                          elevation={0}
                          onClick={() => handleSelectEmployee(emp)}
                          sx={{
                            p: 1.25,
                            px: 1.5,
                            borderRadius: "12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            transition: "all 0.2s ease",
                            flexShrink: 0,
                            backgroundColor: isSelected
                              ? isDark
                                ? alpha("#3b82f6", 0.18)
                                : alpha("#3b82f6", 0.1)
                              : isDark
                                ? alpha("#1e293b", 0.3)
                                : "#ffffff",
                            border: `1px solid ${isSelected
                                ? alpha("#3b82f6", 0.4)
                                : isDark
                                  ? alpha("#cbd5e1", 0.05)
                                  : alpha("#e2e8f0", 0.6)
                              }`,
                            "&:hover": {
                              backgroundColor: isSelected
                                ? isDark
                                  ? alpha("#3b82f6", 0.22)
                                  : alpha("#3b82f6", 0.15)
                                : isDark
                                  ? alpha("#1e293b", 0.7)
                                  : alpha("#f1f5f9", 0.8),
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Box sx={{ position: "relative" }}>
                            <Avatar
                              src={emp.avatar || emp.profile_image}
                              sx={{
                                width: 38,
                                height: 38,
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                backgroundColor: avatarBg,
                                color: "#ffffff",
                              }}
                            >
                              {getInitials(emp.first_name, emp.last_name)}
                            </Avatar>
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: "#22c55e",
                                border: `2px solid ${isDark ? "#0f172a" : "#ffffff"
                                  }`,
                              }}
                            />
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              noWrap
                              sx={{
                                fontWeight: isSelected ? 700 : 600,
                                fontSize: "0.85rem",
                                color: isSelected
                                  ? isDark
                                    ? "#60a5fa"
                                    : "#1d4ed8"
                                  : isDark
                                    ? "#f3f4f6"
                                    : "#0f172a",
                              }}
                            >
                              {empName}
                            </Typography>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.72rem",
                                display: "block",
                              }}
                            >
                              {empRole}
                            </Typography>
                          </Box>
                        </Paper>
                      );
                    })
                  )}
                </>
              )}
            </>
          ) : (
            /* GROUPS TAB CONTENT */
            <>
              {(() => {
                const filteredGroups = groupConversations.filter((conv) => {
                  if (!isSearching) return true;
                  const query = searchQuery.toLowerCase();
                  const name = (conv.name || "").toLowerCase();
                  return name.includes(query);
                });

                return (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1.5,
                        pt: 0.5,
                        pb: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.68rem",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: isDark ? "#94a3b8" : "#64748b",
                        }}
                      >
                        Groups ({filteredGroups.length})
                      </Typography>
                    </Box>

                    {filteredGroups.length === 0 ? (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: "transparent",
                          border: `1px dashed ${isDark
                              ? alpha("#cbd5e1", 0.15)
                              : alpha("#e2e8f0", 0.8)
                            }`,
                          borderRadius: "12px",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          {isSearching
                            ? `No groups matching "${searchQuery}"`
                            : "No group chats available."}
                        </Typography>
                      </Paper>
                    ) : (
                      filteredGroups.map((conv) => {
                        const isProject =
                          conv.type === "project" || Boolean(conv.projectId);
                        const isRemoved = Boolean(conv.isRemoved);
                        const displayName =
                          conv.name ||
                          (isProject ? "Project Group" : "Group Chat");
                        const displayAvatar = conv.avatar;

                        const isSelected =
                          !selectedUser.isAi &&
                          String(selectedUser.conversationId) ===
                          String(conv._id);

                        const unreadCount =
                          conv.unreadCounts?.[currentUserId] || 0;
                        const lastMsgSender = conv.lastMessage?.senderName
                          ? String(conv.lastMessage.senderId) === currentUserId
                            ? "You"
                            : conv.lastMessage.senderName
                          : "";
                        const lastMsgText = conv.lastMessage?.text
                          ? lastMsgSender
                            ? `${lastMsgSender}: ${conv.lastMessage.text}`
                            : conv.lastMessage.text
                          : "Group created";
                        const lastMsgTime = conv.lastMessage?.createdAt
                          ? new Date(
                            conv.lastMessage.createdAt,
                          ).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                          : "";

                        return (
                          <Paper
                            key={conv._id}
                            elevation={0}
                            onClick={() => handleSelectConversation(conv)}
                            sx={{
                              p: 1.25,
                              px: 1.5,
                              borderRadius: "12px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              transition: "all 0.2s ease",
                              flexShrink: 0,
                              backgroundColor: isSelected
                                ? isDark
                                  ? alpha("#3b82f6", 0.18)
                                  : alpha("#3b82f6", 0.1)
                                : isDark
                                  ? alpha("#1e293b", 0.3)
                                  : "#ffffff",
                              border: `1px solid ${isSelected
                                  ? alpha("#3b82f6", 0.4)
                                  : isDark
                                    ? alpha("#cbd5e1", 0.05)
                                    : alpha("#e2e8f0", 0.6)
                                }`,
                              "&:hover": {
                                backgroundColor: isSelected
                                  ? isDark
                                    ? alpha("#3b82f6", 0.22)
                                    : alpha("#3b82f6", 0.15)
                                  : isDark
                                    ? alpha("#1e293b", 0.7)
                                    : alpha("#f1f5f9", 0.8),
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <Box sx={{ position: "relative" }}>
                              <Avatar
                                src={displayAvatar}
                                sx={{
                                  width: 38,
                                  height: 38,
                                  fontSize: "0.85rem",
                                  fontWeight: 700,
                                  backgroundColor: isProject
                                    ? "#8b5cf6"
                                    : getAvatarColor(displayName),
                                  color: "#ffffff",
                                }}
                              >
                                {isProject ? (
                                  <ProjectIcon sx={{ fontSize: 20 }} />
                                ) : (
                                  <GroupIcon sx={{ fontSize: 20 }} />
                                )}
                              </Avatar>
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  noWrap
                                  sx={{
                                    fontWeight: isSelected ? 700 : 600,
                                    fontSize: "0.85rem",
                                    color: isSelected
                                      ? isDark
                                        ? "#60a5fa"
                                        : "#1d4ed8"
                                      : isDark
                                        ? "#f3f4f6"
                                        : "#0f172a",
                                  }}
                                >
                                  {displayName}
                                </Typography>
                                {isRemoved ? (
                                  <Chip
                                    label="Removed"
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: "0.58rem",
                                      fontWeight: 800,
                                      backgroundColor: alpha("#ef4444", 0.15),
                                      color: "#ef4444",
                                      border: `1px solid ${alpha(
                                        "#ef4444",
                                        0.3,
                                      )}`,
                                      px: 0.25,
                                    }}
                                  />
                                ) : isProject ? (
                                  <Chip
                                    label="Project"
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: "0.58rem",
                                      fontWeight: 800,
                                      backgroundColor: alpha("#8b5cf6", 0.15),
                                      color: "#8b5cf6",
                                      border: `1px solid ${alpha(
                                        "#8b5cf6",
                                        0.3,
                                      )}`,
                                      px: 0.25,
                                    }}
                                  />
                                ) : lastMsgTime ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.68rem",
                                      color: "text.secondary",
                                    }}
                                  >
                                    {lastMsgTime}
                                  </Typography>
                                ) : null}
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mt: 0.25,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: "0.72rem",
                                    maxWidth: "180px",
                                  }}
                                >
                                  {lastMsgText}
                                </Typography>
                                {unreadCount > 0 && (
                                  <Chip
                                    label={unreadCount}
                                    size="small"
                                    sx={{
                                      height: 16,
                                      minWidth: 16,
                                      fontSize: "0.62rem",
                                      fontWeight: 800,
                                      backgroundColor: "#3b82f6",
                                      color: "#ffffff",
                                      px: 0.5,
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })
                    )}
                  </>
                );
              })()}
            </>
          )}
        </Box>
      </Box>

      {/* RIGHT CHAT AREA */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          flex: 1,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Header - Fixed */}
        {!selectedUser.isAi && (
          <Box
            sx={{
              flexShrink: 0,
              py: 1.75,
              px: { xs: 2, md: 3 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: isDark
                ? alpha("#0f172a", 0.85)
                : alpha("#ffffff", 0.85),
              backdropFilter: "blur(12px)",
              borderBottom: `1px solid ${isDark ? alpha("#cbd5e1", 0.08) : alpha("#e2e8f0", 0.8)
                }`,
              zIndex: 10,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {selectedUser.isAi ? (
                <Box sx={{ position: "relative" }}>
                  <Avatar
                    src={aiChatIcon}
                    sx={{
                      width: 42,
                      height: 42,
                      boxShadow: "0 0 16px rgba(245, 158, 11, 0.35)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 1,
                      right: 1,
                      width: 11,
                      height: 11,
                      borderRadius: "50%",
                      backgroundColor: "#22c55e",
                      border: `2px solid ${isDark ? "#0f172a" : "#ffffff"}`,
                      boxShadow: "0 0 6px #22c55e",
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ position: "relative" }}>
                  <Avatar
                    src={selectedUser.avatar}
                    sx={{
                      width: 42,
                      height: 42,
                      fontSize: "0.95rem",
                      fontWeight: 700,
                      backgroundColor: getAvatarColor(selectedUser.name),
                      color: "#ffffff",
                    }}
                  >
                    {getInitials(
                      selectedUser.name?.split(" ")[0],
                      selectedUser.name?.split(" ")[1],
                    )}
                  </Avatar>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 1,
                      right: 1,
                      width: 11,
                      height: 11,
                      borderRadius: "50%",
                      backgroundColor: "#22c55e",
                      border: `2px solid ${isDark ? "#0f172a" : "#ffffff"}`,
                    }}
                  />
                </Box>
              )}

              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.05rem",
                      letterSpacing: "-0.01em",
                      color: isDark ? "#f3f4f6" : "#0f172a",
                    }}
                  >
                    {selectedUser.name}
                  </Typography>
                  {selectedUser.isRemoved ? (
                    <Chip
                      label="Removed (Read-Only)"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        backgroundColor: alpha("#ef4444", 0.15),
                        color: "#ef4444",
                        border: `1px solid ${alpha("#ef4444", 0.3)}`,
                      }}
                    />
                  ) : selectedUser.isProject ? (
                    <Chip
                      label="Project Group"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        backgroundColor: alpha("#8b5cf6", 0.15),
                        color: "#8b5cf6",
                        border: `1px solid ${alpha("#8b5cf6", 0.3)}`,
                      }}
                    />
                  ) : (
                    <Chip
                      label="Active"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        letterSpacing: "0.03em",
                        backgroundColor: alpha("#22c55e", 0.12),
                        color: isDark ? "#4ade80" : "#15803d",
                        border: `1px solid ${alpha("#22c55e", 0.25)}`,
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.78rem",
                    display: "block",
                  }}
                >
                  {selectedUser.isAi
                    ? "Intelligent automation for leave and workforce actions"
                    : selectedUser.role ||
                    selectedUser.email ||
                    "Employee Direct Messaging"}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Messages Area - Scrollable */}
        <Box
          ref={messagesContainerRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, sm: 3, md: 4 },
            py: 3,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDark
                ? alpha("#94a3b8", 0.2)
                : alpha("#94a3b8", 0.3),
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          <Box
            sx={{
              height: '100%',
              maxWidth: "860px",
              width: "100%",
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {selectedUser.isAi ? (
              /* AI CHAT - Coming Soon */
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  gap: 2,
                  p: 4,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: isDark
                      ? alpha("#f59e0b", 0.15)
                      : alpha("#f59e0b", 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <ForumIcon sx={{ fontSize: 40, color: "#f59e0b" }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: isDark ? "#f3f4f6" : "#0f172a",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Chat with your Teams 🚀
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    maxWidth: 420,
                  }}
                >
                  To start chatting select a team or project from the sidebar. Use the search bar to find a team or project.
                </Typography>
                <Chip
                  label="Start Chatting"
                  sx={{
                    mt: 1,
                    backgroundColor: isDark
                      ? alpha("#f59e0b", 0.15)
                      : alpha("#f59e0b", 0.1),
                    color: "#f59e0b",
                    fontWeight: 600,
                    border: `1px solid ${alpha("#f59e0b", 0.3)}`,
                  }}
                />
              </Box>
            ) : (
              // EMPLOYEE CHAT - Keep this as is

              /* EMPLOYEE 1-ON-1 MESSAGES FROM REDUX STORE */
              <>
                {loadingMessages ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      py: 8,
                      gap: 2,
                    }}
                  >
                    <CircularProgress
                      size={32}
                      sx={{ color: "primary.main" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Loading chat history...
                    </Typography>
                  </Box>
                ) : currentEmployeeMessages.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 8,
                      px: 2,
                      textAlign: "center",
                    }}
                  >
                    <Avatar
                      src={selectedUser.avatar}
                      sx={{
                        width: 72,
                        height: 72,
                        mb: 2,
                        fontSize: "1.75rem",
                        fontWeight: 700,
                        backgroundColor: getAvatarColor(selectedUser.name),
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                      }}
                    >
                      {getInitials(
                        selectedUser.name?.split(" ")[0],
                        selectedUser.name?.split(" ")[1],
                      )}
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        color: isDark ? "#f3f4f6" : "#0f172a",
                      }}
                    >
                      Direct Chat with {selectedUser.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mb: 2 }}
                    >
                      {selectedUser.role || selectedUser.email || "Employee"}
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: "16px",
                        maxWidth: "420px",
                        backgroundColor: isDark
                          ? alpha("#1e293b", 0.6)
                          : alpha("#f1f5f9", 0.8),
                        border: `1px dashed ${isDark
                            ? alpha("#cbd5e1", 0.15)
                            : alpha("#cbd5e1", 0.4)
                          }`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        Type a message below to start a direct conversation with{" "}
                        {selectedUser.name}.
                      </Typography>
                    </Paper>
                  </Box>
                ) : (
                  <AnimatePresence initial={false}>
                    {currentEmployeeMessages.map((msg) => {
                      const isUser = msg.sender === "user";
                      const isGroupChat = Boolean(
                        selectedUser.isGroup || selectedUser.isProject,
                      );

                      let avatarSrc = "";
                      let avatarName = "";
                      let avatarInitials = "";

                      if (isUser) {
                        avatarSrc = currentUserAvatar;
                        avatarName = currentUserName;
                        avatarInitials = "You";
                      } else if (isGroupChat) {
                        avatarSrc = msg.senderAvatar;
                        avatarName = msg.senderName || "Member";
                        avatarInitials = getInitialsFromName(avatarName);
                      } else {
                        avatarSrc = msg.senderAvatar || selectedUser.avatar;
                        avatarName = selectedUser.name;
                        avatarInitials = getInitialsFromName(selectedUser.name);
                      }

                      return (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                          style={{
                            display: "flex",
                            justifyContent: isUser ? "flex-end" : "flex-start",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: isUser ? "row-reverse" : "row",
                              gap: 1.5,
                              maxWidth: { xs: "90%", sm: "80%" },
                            }}
                          >
                            <Avatar
                              src={avatarSrc}
                              sx={{
                                width: 34,
                                height: 34,
                                mt: 0.25,
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                background: isUser
                                  ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                                  : getAvatarColor(avatarName),
                                color: "#ffffff",
                              }}
                            >
                              {avatarInitials}
                            </Avatar>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              {isGroupChat && !isUser && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    color: isDark ? "#60a5fa" : "#2563eb",
                                    px: 0.5,
                                    alignSelf: "flex-start",
                                    mb: -0.25,
                                  }}
                                >
                                  {msg.senderName || "Member"}
                                </Typography>
                              )}
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 1.75,
                                  px: 2.25,
                                  borderRadius: isUser
                                    ? "18px 4px 18px 18px"
                                    : "4px 18px 18px 18px",
                                  backgroundColor: isUser
                                    ? isDark
                                      ? "#2563eb"
                                      : "#3b82f6"
                                    : isDark
                                      ? alpha("#1e293b", 0.9)
                                      : "#ffffff",
                                  color: isUser
                                    ? "#ffffff"
                                    : isDark
                                      ? "#f3f4f6"
                                      : "#1f2937",
                                  border: isUser
                                    ? "none"
                                    : `1px solid ${isDark
                                      ? alpha("#334155", 0.6)
                                      : alpha("#e2e8f0", 0.9)
                                    }`,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ lineHeight: 1.6 }}
                                >
                                  {msg.text}
                                </Typography>
                              </Paper>
                              <Typography
                                variant="caption"
                                sx={{
                                  px: 1,
                                  fontSize: "0.7rem",
                                  color: isDark ? "#64748b" : "#94a3b8",
                                  alignSelf: isUser ? "flex-end" : "flex-start",
                                }}
                              >
                                {msg.timestamp}
                              </Typography>
                            </Box>
                          </Box>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                )}
              </>
            )}
            <div ref={chatEndRef} />
          </Box>
        </Box>

        {/* Input Footer - Fixed */}
        <Box
          sx={{
            flexShrink: 0,
            backgroundColor: isDark
              ? alpha("#0f172a", 0.95)
              : alpha("#ffffff", 0.95),
            backdropFilter: "blur(16px)",
            borderTop: `1px solid ${isDark ? alpha("#cbd5e1", 0.08) : alpha("#e2e8f0", 0.8)
              }`,
            px: { xs: 2, sm: 3, md: 4 },
            py: 2,
          }}
        >
          <Box
            sx={{
              maxWidth: "860px",
              width: "100%",
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {selectedUser.isRemoved ? (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "16px",
                  backgroundColor: isDark
                    ? alpha("#ef4444", 0.12)
                    : alpha("#ef4444", 0.08),
                  border: `1px solid ${alpha("#ef4444", 0.3)}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  color: isDark ? "#fca5a5" : "#991b1b",
                }}
              >
                <LockIcon sx={{ fontSize: 22, color: "#ef4444" }} />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, fontSize: "0.875rem" }}
                  >
                    Read-Only Access
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.78rem",
                      display: "block",
                      color: isDark ? "#f87171" : "#b91c1c",
                    }}
                  >
                    You were removed from this project group. You can view
                    previous conversation history, but cannot post new messages.
                  </Typography>
                </Box>
              </Paper>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder={
                    selectedUser.isAi
                      ? "Ask AI anything or request an action..."
                      : `Type a message to ${selectedUser.name}...`
                  }
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  variant="outlined"
                  size="small"
                  disabled={aiLoading && selectedUser.isAi}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                      backgroundColor: isDark ? "#1e293b" : "#f8fafc",
                      color: isDark ? "#f3f4f6" : "#1f2937",
                      px: 2,
                      py: 1,
                      fontSize: "0.935rem",
                      "& fieldset": {
                        borderColor: isDark
                          ? alpha("#334155", 0.8)
                          : alpha("#cbd5e1", 0.8),
                        transition:
                          "border-color 0.2s ease, box-shadow 0.2s ease",
                      },
                      "&:hover fieldset": {
                        borderColor: selectedUser.isAi ? "#f59e0b" : "#3b82f6",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: selectedUser.isAi ? "#f59e0b" : "#3b82f6",
                        boxShadow: selectedUser.isAi
                          ? "0 0 12px rgba(245, 158, 11, 0.25)"
                          : "0 0 12px rgba(59, 130, 246, 0.25)",
                      },
                    },
                  }}
                />

                <IconButton
                  onClick={() => handleSendMessage()}
                  disabled={
                    !inputMessage.trim() || (selectedUser.isAi)
                  }
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "14px",
                    flexShrink: 0,
                    background: selectedUser.isAi
                      ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                      : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                    color: "#ffffff",
                    boxShadow: selectedUser.isAi
                      ? "0 4px 14px rgba(245, 158, 11, 0.35)"
                      : "0 4px 14px rgba(59, 130, 246, 0.35)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      background: selectedUser.isAi
                        ? "linear-gradient(135deg, #d97706 0%, #b45309 100%)"
                        : "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                      transform: "translateY(-2px)",
                    },
                    "&.Mui-disabled": {
                      background: isDark ? "#334155" : "#e2e8f0",
                      color: isDark ? "#64748b" : "#94a3b8",
                      boxShadow: "none",
                    },
                  }}
                >
                  <SendIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            )}

            <Typography
              variant="caption"
              sx={{
                textAlign: "center",
                fontSize: "0.72rem",
                color: isDark ? "#64748b" : "#94a3b8",
              }}
            >
              Press{" "}
              <strong style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                Enter
              </strong>{" "}
              to send •{" "}
              <strong style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                Shift + Enter
              </strong>{" "}
              for new line
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Chat;
